import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Search, Eye, Edit, Trash2, X, Plus } from "lucide-react";
import Button from "../../components/ui/Button";
import DataTable from "../../components/ui/DataTable";
import { Badge } from "../../components/ui/Badge";
import {
  useGetDistributorsQuery,
  useDeleteDistributorMutation,
  useUpdateDistributorMutation,
  useCreateDistributorMutation,
} from "../../redux/api/distributor";
import { useLanguage } from "../../contexts/LanguageContext";
import { useFormatters } from "../../hooks/useFormatters";
import { FormField, FormInput, FormSelect } from "../../components/ui/Form";
import DistributorDetailModal from "../../components/modals/DistributorDetailModal";
import DistributorModal from "../../components/modals/DistributorModal";
import { MySwal } from "../../components/ui/MySwal";
import { addToast } from "../../redux/slices/toastSlice";
import { useErrorHandler } from "../../hooks/useErrorHandler";
import { useAuth } from "../../hooks/useAuth";

const initialFilters = {
  code: "",
  name: "",
  status: "all",
};

const Distributors = () => {
  const dispatch = useDispatch();
  const { t } = useLanguage();
  const { formatNumber } = useFormatters();
  const { handleError } = useErrorHandler();
  const { isHq, user } = useAuth();

  // Local UI state
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [selectedDistributor, setSelectedDistributor] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [filters, setFilters] = useState(initialFilters);
  const [filterlist, setFilterlist] = useState(null);

  const {
    data: distributors,
    isLoading,
    error,
  } = useGetDistributorsQuery({
    page: currentPage,
    pageSize: itemsPerPage,
    search: filterlist,
  });
  const [createDistributor] = useCreateDistributorMutation();
  const [updateDistributor] = useUpdateDistributorMutation();
  const [deleteDistributor] = useDeleteDistributorMutation();

  // Filter and sort distributors
  const filteredDistributors = useMemo(() => {
    const distributorData = distributors?.data || [];
    let filtered = [...distributorData];

    filtered.sort((a, b) => {
      const aVal = (a?.[sortBy] ?? "").toString();
      const bVal = (b?.[sortBy] ?? "").toString();
      return sortOrder === "asc"
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    });

    return filtered;
  }, [distributors, sortBy, sortOrder]);

  useEffect(() => {
    setFilterlist({
      distributorId: user?.distributorId || null,
      ...initialFilters
    })
  }, [user])

  // Columns configuration
  const columns = [
    {
      key: "code",
      header: t("distributor.code", "Code"),
      render: (value) => <div className="font-medium">{value}</div>,
    },
    {
      key: "name",
      header: t("distributor.name", "Name"),
      render: (value, d) => (
        <div className="flex items-center gap-3">
          <div>
            <p className="font-medium">{value}</p>
            <p className="text-sm text-muted-foreground">{d.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: "prefix",
      header: t("distributor.prefix", "Prefix"),
    },
    {
      key: "contactName",
      header: t("distributor.contact", "Contact"),
      sortable: false,
      render: (value, d) => (
        <div>
          <div>{value || "-"}</div>
          {d.contactAdmin && (
            <div className="text-xs text-muted-foreground">
              Admin: {d.contactAdmin}
            </div>
          )}
        </div>
      ),
    },
    {
      key: "addr4",
      header: t("customer.district"),
      minWidth: "90px",
      sortable: false,
      render: (value, customer) =>
        `${value === "รอข้อมูล" || !value ? "-" : value}` || "-",
    },
    {
      key: "addr5",
      header: t("customer.city", "City"),
      minWidth: "90px",
      sortable: false,
      render: (value, customer) => (
        <div>{`${value === "รอข้อมูล" || !value ? "-" : value}` || "-"}</div>
      ),
    },
    {
      key: "isActive",
      header: t("common.status", "Status"),
      sortable: false,
      render: (value) => (
        <Badge variant={value === false ? "secondary" : "default"}>
          {value === false
            ? t("common.inactive", "Inactive")
            : t("common.active", "Active")}
        </Badge>
      ),
    },
    {
      key: "actions",
      header: "",
      sortable: false,
      render: (_, distributor) => (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedDistributor(distributor?.id || 0);
              setShowDetailModal(true);
            }}
            title={t("common.view", "View")}
          >
            <Eye className="h-4 w-4" />
          </Button>

          {isHq && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleEditModal(distributor)}
                title={t("common.edit", "Edit")}
              >
                <Edit className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDeleteDistributor(distributor?.id || 0)}
                title={t("common.delete", "Delete")}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  const handleSort = (key, order) => {
    setSortBy(key);
    setSortOrder(order);
  };

  const handleEditModal = (distributor) => {
    setSelectedDistributor(distributor);
    setShowEditModal(true);
  };

  const handleDeleteDistributor = (id) => {
    const confirmButton = MySwal.fire({
      text: t("alert.deleteWarning"),
      icon: "warning",
      showCancelButton: true,
      position: "center",
    });
    confirmButton.then((result) => {
      if (result.isConfirmed) {
        deleteDistributor(id);
      }
    });
  };

  const handleCloseModal = () => {
    setCurrentPage(1);
    setSelectedDistributor(null);
    setShowDetailModal(false);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setFilterlist(filters);
  };

  const handleSaveDistributor = async (distributorData) => {
    const distributorId = distributorData.id;
    const handleApiCall = async () => {
      if (distributorId) {
        return updateDistributor({
          id: distributorId,
          payload: distributorData,
        }).unwrap();
      } else {
        return createDistributor(distributorData).unwrap();
      }
    };
    try {
      const confirmButton = await MySwal.fire({
        text: distributorId ? t("alert.updateInfo") : t("alert.createInfo"),
        icon: "info",
        showCancelButton: true,
        position: "center",
      });

      if (confirmButton.isConfirmed) {
        const result = await handleApiCall();
        if (result) {
          setShowEditModal(false);
          setSelectedDistributor(null);
          setCurrentPage(1);
        }
      }
    } catch (error) {
      handleError(error, {
        id: Date.now().toString(),
        message: t("alert.updateError"),
        type: "error",
      });
    }
    if (distributorData) return;
  };

  const customPagination = useMemo(() => {
    const totalItems = distributors ? distributors.meta?.total : 0;
    const totalPages = distributors ? distributors.meta?.totalPages : 1;
    const handlePageChange = (page) => {
      setCurrentPage(Math.max(1, Math.min(page, totalPages)));
    };
    const handleItemsPerPageChange = (itemsPerPage) => {
      setItemsPerPage(itemsPerPage);
      setCurrentPage(1);
    };
    return {
      currentPage: currentPage,
      totalPages: totalPages,
      totalItems: totalItems,
      itemsPerPage: itemsPerPage,
      onPageChange: handlePageChange,
      onItemsPerPageChange: handleItemsPerPageChange,
    };
  }, [currentPage, itemsPerPage, distributors]);

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            {t("navigation.distributors", "Distributors")}
          </h1>
          <p className="text-muted-foreground">
            {t("distributor.description", "Manage distributors and settings")}
          </p>
        </div>
        {isHq && (
        <div className="flex items-center gap-2">
          <Button onClick={() => setShowEditModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            {t("distributor.addDistributor")}
          </Button>
        </div> )}
      </div>

      {/* Search and filters */}
      {/* <div>
        <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          <FormField label={t("distributor.code", "Code")}>
            <FormInput
              type="text"
              name="code"
              value={filters.code}
              onChange={handleFilterChange}
            />
          </FormField>

          <FormField label={t("distributor.name", "Name")}>
            <FormInput
              type="text"
              name="name"
              value={filters.name}
              onChange={handleFilterChange}
            />
          </FormField>
        </div>

        <div className="flex items-center gap-4 mt-4">
          <Button
            variant="outline"
            onClick={handleSearch}
            disabled={isLoading || filters === initialFilters}
          >
            <Search className="h-4 w-4 mr-2" />
            {t("common.search", "Search")}
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              setFilterlist(null);
              setFilters(initialFilters);
            }}
            disabled={isLoading || filters === initialFilters}
          >
            <X className="h-4 w-4 mr-2" />
            {t("common.clear", "Clear")}
          </Button>
        </div>
      </div> */}

      {/* Distributors table */}
      <DataTable
        data={filteredDistributors}
        columns={columns}
        loading={isLoading}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        searchable={false}
        emptyMessage={t("distributor.empty", "No distributors found")}
        pagination={isHq}
        customPagination={customPagination}
      />

      {/* Detail Modal */}
      <DistributorDetailModal
        id={selectedDistributor}
        onClose={handleCloseModal}
        isOpen={showDetailModal}
      />
      {/* Edit Modal */}
      <DistributorModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedDistributor(null);
        }}
        onSave={handleSaveDistributor}
        distributor={selectedDistributor}
        isLoading={isLoading}
      />
    </div>
  );
};

export default Distributors;
